<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useUserSkills } from '../composables/useUserSkills';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  position: {
    type: Object,
    default: () => ({ x: 0, y: 0 }),
  },
});

const emit = defineEmits(['close', 'select']);

// State
const skills = ref([]);
const loading = ref(false);
const error = ref(null);
const searchQuery = ref('');
const popoverRef = ref(null);
const selectedIndex = ref(0);
const inputRef = ref(null);
const itemRefs = ref([]);

const { listPublicSkills } = useUserSkills();

// Constants
const POPOVER_WIDTH = 320;
const VIEWPORT_PADDING = 16;

// Watchers
watch(
  () => props.show,
  async (show) => {
    if (show) {
      selectedIndex.value = 0;
      if (skills.value.length === 0) {
        await fetchSkills();
      }
      nextTick(() => {
        inputRef.value?.focus();
      });
    }
  }
);

watch(searchQuery, () => {
  selectedIndex.value = 0;
});

// Keydown handler
const handleKeydown = (e) => {
  if (filteredSkills.value.length === 0) return;

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    selectedIndex.value = (selectedIndex.value + 1) % filteredSkills.value.length;
    scrollToSelected();
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    selectedIndex.value =
      (selectedIndex.value - 1 + filteredSkills.value.length) % filteredSkills.value.length;
    scrollToSelected();
  } else if (e.key === 'Enter') {
    e.preventDefault();
    handleSelect(filteredSkills.value[selectedIndex.value]);
  }
};

const scrollToSelected = () => {
  nextTick(() => {
    const el = itemRefs.value[selectedIndex.value];
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  });
};

// Actions
const fetchSkills = async () => {
  loading.value = true;
  error.value = null;
  try {
    const res = await listPublicSkills({ q: searchQuery.value, limit: 50 });
    // Assuming the response is an array or has a data property that is an array
    skills.value = res.rows || res || [];
  } catch (err) {
    console.error('Error fetching skills:', err);
    error.value = 'Failed to load skills';
  } finally {
    loading.value = false;
  }
};

const filteredSkills = computed(() => {
  if (!searchQuery.value) return skills.value;
  const q = searchQuery.value.toLowerCase();
  return skills.value.filter(
    (s) =>
      s.name.toLowerCase().includes(q) || (s.description && s.description.toLowerCase().includes(q))
  );
});

const handleSelect = (skill) => {
  emit('select', skill);
  emit('close');
};

// Safe position calculation
const safePosition = computed(() => {
  const { x, y } = props.position;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  const halfWidth = POPOVER_WIDTH / 2;
  let safeX = x;

  if (x - halfWidth < VIEWPORT_PADDING) {
    safeX = halfWidth + VIEWPORT_PADDING;
  }
  if (x + halfWidth > viewportWidth - VIEWPORT_PADDING) {
    safeX = viewportWidth - halfWidth - VIEWPORT_PADDING;
  }

  const spaceBelow = viewportHeight - y - VIEWPORT_PADDING;
  // If space below is less than 400px and space above is more than space below, show above
  const showAbove = spaceBelow < 400 && y > spaceBelow;

  return { x: safeX, y, showAbove };
});

const popoverStyle = computed(() => {
  const { x, y, showAbove } = safePosition.value;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  const style = {
    left: `${x - POPOVER_WIDTH / 2}px`,
    width: `${POPOVER_WIDTH}px`,
  };

  if (showAbove) {
    style.bottom = `${viewportHeight - y + 8}px`;
  } else {
    style.top = `${y + 8}px`;
  }

  return style;
});
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="fixed inset-0 z-[99]" @click="emit('close')" />

    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="show"
        ref="popoverRef"
        class="fixed z-[100] bg-white rounded-2xl border-[0.5px] border-black/15 shadow-[0_8px_30px_rgb(0,0,0,0.12)] overflow-hidden flex flex-col max-h-[400px]"
        :style="popoverStyle"
        @click.stop
      >
        <!-- Header / Search -->
        <div class="p-3 border-b border-black/5">
          <div class="relative flex items-center">
            <input
              ref="inputRef"
              v-model="searchQuery"
              type="text"
              placeholder="Search skills..."
              class="w-full h-9 pl-8 pr-3 text-xs border border-solid border-black/[0.02] dark:border-white/[0.02] bg-black/5 dark:bg-white/5 rounded-lg shadow-none placeholder:text-black/30 dark:placeholder:text-white/30 focus:ring-0 focus-visible:ring-0 focus:outline-none focus-visible:outline-none"
              @input="fetchSkills"
              @keydown="handleKeydown"
            />
            <svg
              class="absolute left-2.5 w-3.5 h-3.5 text-black/30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto min-h-[100px] p-2 custom-scrollbar">
          <!-- Loading -->
          <div
            v-if="loading && skills.length === 0"
            class="flex flex-col items-center justify-center py-8 gap-2"
          >
            <div
              class="w-5 h-5 border-2 border-black/10 border-t-black/40 rounded-full animate-spin"
            />
            <span class="text-[10px] text-black/40">Loading skills...</span>
          </div>

          <!-- Empty -->
          <div
            v-else-if="filteredSkills.length === 0"
            class="flex flex-col items-center justify-center py-8 text-black/30"
          >
            <svg
              class="w-8 h-8 mb-2 opacity-20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <span class="text-[10px]">No skills found</span>
          </div>

          <!-- List -->
          <div v-else class="grid grid-cols-1 gap-1">
            <button
              v-for="(skill, index) in filteredSkills"
              :key="skill.id"
              :ref="
                (el) => {
                  if (el) itemRefs[index] = el;
                }
              "
              class="group flex items-center gap-3 p-2 rounded-xl transition-all text-left"
              :class="{
                'bg-black/[0.05]': index === selectedIndex,
                'hover:bg-black/[0.03]': index !== selectedIndex,
              }"
              @click="handleSelect(skill)"
              @mousemove="selectedIndex = index"
            >
              <div class="w-10 h-10 rounded-lg bg-black/[0.03] overflow-hidden flex-shrink-0">
                <img
                  v-if="skill.cover"
                  :src="skill.cover"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div v-else class="w-full h-full flex items-center justify-center">
                  <svg
                    class="w-4 h-4 text-black/10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  </svg>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-[11px] font-bold text-black/80 truncate">{{ skill.name }}</div>
                <div class="text-[10px] text-black/40 truncate">
                  {{ skill.category || 'General' }}
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.1);
}
</style>
